import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, FormField, FormHelp, Input, InputChip, Label, Toast, useToast } from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';

import {
  useRentalManagerUserPropertyApartmentAmenityWebApiHandler_create,
  useRentalManagerUserPropertyApartmentAmenityWebApiHandler_delete,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { CharacterCountFormHelp } from '@zg-rentals/visual-cohesion';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import { useAuth } from '../../../AuthProvider';

import type { DatarouterError } from '@zg-rentals/rental-manager-env';

const AdditionalUnitAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails, isFetching: isFetchingListingDetails } = useGetListingDetails(listingAlias);

  const apartmentAmenities = listingDetails?.apartmentAmenities || [];
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [amenityText, setAmenityText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: createAmenity, isPending: isCreating } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyApartmentAmenityWebApiHandler_create,
    successToastMessage: 'Amenity added',
    meta: { context: { api: 'apartmentAmenity.create', details: { listingAlias } } },
  });

  const { mutateAsync: deleteAmenity, isPending: isDeleting } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyApartmentAmenityWebApiHandler_delete,
    successToastMessage: 'Amenity deleted',
    meta: { context: { api: 'apartmentAmenity.delete', details: { listingAlias } } },
  });

  const isLoading = isCreating || isDeleting || isFetchingListingDetails;

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAmenityText = event.target.value;

    setAmenityText(newAmenityText);
    setErrorMessage('');
  };

  const handleOnBlur = async () => {
    setIsFocused(false);

    const valueToSave = amenityText.trim();
    if (valueToSave.length === 0 || valueToSave.length > 100) {
      return;
    }

    // Check for duplicates
    const tagsMap = apartmentAmenities.reduce(
      (acc, curVal) => {
        acc[curVal.amenity.toLowerCase()] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    if (tagsMap[valueToSave.toLowerCase()]) {
      setErrorMessage('You’ve already entered this amenity');
      return;
    }

    try {
      await createAmenity({
        getParams: {
          text: valueToSave,
          listingAlias,
        },
      });
      setAmenityText('');
      setErrorMessage('');
    } catch (err) {
      const message =
        (err as DatarouterError)?.message ??
        'There was an error with creating the amenity, refresh the page and try again';
      enqueueToast(<Toast tone="critical">{message}</Toast>);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    setErrorMessage('');

    if (event.key === 'Enter') {
      event.preventDefault();
      handleOnBlur();
    }
  };

  const onTagClose = async (amenityId: string) => {
    try {
      await deleteAmenity({
        getParams: {
          amenityId,
        },
      });
    } catch (err) {
      const message =
        (err as DatarouterError)?.message ??
        'There was an error with deleting the amenity, refresh the page and try again';
      enqueueToast(<Toast tone="critical">{message}</Toast>);
    }
  };

  return (
    <Box
      data-testid="AdditionalUnitAmenities"
      css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: '1 0 280px' }}
    >
      <Box css={{ display: 'flex', flexDirection: 'row' }}>
        <Box css={{ flex: { xl: 'auto', base: '0 1 420px' } }}>
          <FormField
            error={!!errorMessage || amenityText.length > 100}
            disabled={isInputFormDisabled || isLoading}
            label={<Label>Additional unit amenities</Label>}
            control={
              <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
                <Input
                  name="amenityText"
                  value={amenityText}
                  onChange={handleOnChange}
                  onBlur={handleOnBlur}
                  onKeyDown={handleKeyPress}
                  onFocus={() => setIsFocused(true)}
                />
              </Box>
            }
            description={
              errorMessage ? (
                <FormHelp>{errorMessage}</FormHelp>
              ) : (
                <CharacterCountFormHelp
                  isFocused={isFocused}
                  currentLength={amenityText.length}
                  maxLength={100}
                  label="Amenity text"
                />
              )
            }
          />
          <Button
            tone="brand"
            emphasis="filled"
            loading={isLoading}
            disabled={isInputFormDisabled}
            icon={<IconPlusCircleFilled />}
            css={{ marginTop: '400' }}
          >
            Add
          </Button>
        </Box>
      </Box>

      {apartmentAmenities.length > 0 && (
        <Box
          css={{
            display: 'flex',
            gap: '200',
            flexWrap: 'wrap',
            opacity: isInputFormDisabled ? 'action.disabled' : 'action.active',
          }}
        >
          {apartmentAmenities.map(({ id, amenity }) => (
            <InputChip
              key={`AdditionalUnitAmenities-Tag-${id}`}
              disabled={isInputFormDisabled}
              isOpen={!isInputFormDisabled}
              onClose={() => onTagClose(id)}
            >
              {amenity}
            </InputChip>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default memo(AdditionalUnitAmenities);
